import { useEffect, useRef } from 'react';
import { Mesh, Program, Renderer, Triangle } from 'ogl';
import { cn } from '@/lib/cn';
import { useReducedMotion } from '@/scroll/use-reduced-motion';

export interface LightfallProps {
  /** Hex colours (up to 8) tinting the falling streaks; each streak takes one at random. */
  colors?: readonly string[];
  /** Hex colour of the soft ambient glow behind the streaks. */
  backgroundColor?: string;
  /** Multiplier for how fast the streaks fall. */
  speed?: number;
  /** Streak layers rendered per cell, 1..16. Higher is busier. */
  streakCount?: number;
  /** Thickness of each streak. */
  streakWidth?: number;
  /** Length of the glowing tail trailing each streak. */
  streakLength?: number;
  /** Brightness multiplier applied before tone mapping. */
  glow?: number;
  /** Vertical frequency of streaks. Higher packs more into view. */
  density?: number;
  /** Per-streak brightness flicker. 0 is constant. */
  twinkle?: number;
  /** Field of view into the tunnel. Higher zooms further in. */
  zoom?: number;
  /** Intensity of the ambient background glow. */
  backgroundGlow?: number;
  /** Alpha of the rendered canvas. */
  opacity?: number;
  /** A soft light that follows the cursor and flares nearby streaks (no warping). */
  mouseInteraction?: boolean;
  /** Intensity of the cursor light. */
  mouseStrength?: number;
  /** Falloff radius of the cursor light. */
  mouseRadius?: number;
  /** Easing time constant (seconds) for the cursor light to follow the pointer. 0 is immediate. */
  mouseDampening?: number;
  /** Freezes the current frame. */
  paused?: boolean;
  /** Render-resolution cap. The shader ray-marches per pixel, so full retina dpr is costly. */
  maxDevicePixelRatio?: number;
  className?: string;
}

const MAX_COLORS = 8;
const DEFAULT_COLORS: readonly string[] = ['#A6C8FF', '#5227FF', '#FF9FFC'];

type Rgb = [number, number, number];

const hexToRgb = (hex: string): Rgb => {
  const c = hex.replace('#', '').padEnd(6, '0');
  return [
    parseInt(c.slice(0, 2), 16) / 255,
    parseInt(c.slice(2, 4), 16) / 255,
    parseInt(c.slice(4, 6), 16) / 255,
  ];
};

/** Pads the palette to MAX_COLORS entries and averages it for the cursor light. */
const prepColors = (input: readonly string[]) => {
  const base = (input.length ? input : DEFAULT_COLORS).slice(0, MAX_COLORS);
  const arr: Rgb[] = [];
  for (let i = 0; i < MAX_COLORS; i++) arr.push(hexToRgb(base[Math.min(i, base.length - 1)]));
  const avg: Rgb = [0, 0, 0];
  for (let i = 0; i < base.length; i++) {
    avg[0] += arr[i][0] / base.length;
    avg[1] += arr[i][1] / base.length;
    avg[2] += arr[i][2] / base.length;
  }
  return { arr, count: base.length, avg };
};

const VERTEX_SHADER = /* glsl */ `
attribute vec2 position;
attribute vec2 uv;
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER = /* glsl */ `
precision highp float;

uniform vec3  iResolution;
uniform vec2  iMouse;
uniform float iTime;

uniform vec3  uColor0;
uniform vec3  uColor1;
uniform vec3  uColor2;
uniform vec3  uColor3;
uniform vec3  uColor4;
uniform vec3  uColor5;
uniform vec3  uColor6;
uniform vec3  uColor7;
uniform int   uColorCount;

uniform vec3  uBgColor;
uniform vec3  uMouseColor;
uniform float uSpeed;
uniform int   uStreakCount;
uniform float uStreakWidth;
uniform float uStreakLength;
uniform float uGlow;
uniform float uDensity;
uniform float uTwinkle;
uniform float uZoom;
uniform float uBgGlow;
uniform float uOpacity;
uniform float uMouseEnabled;
uniform float uMouseStrength;
uniform float uMouseRadius;

varying vec2 vUv;

vec3 palette(float h) {
  int count = uColorCount;
  if (count < 1) count = 1;
  int idx = int(floor(clamp(h, 0.0, 0.999999) * float(count)));
  if (idx <= 0) return uColor0;
  if (idx == 1) return uColor1;
  if (idx == 2) return uColor2;
  if (idx == 3) return uColor3;
  if (idx == 4) return uColor4;
  if (idx == 5) return uColor5;
  if (idx == 6) return uColor6;
  return uColor7;
}

vec3 tanhv(vec3 x) {
  vec3 e = exp(-2.0 * x);
  return (1.0 - e) / (1.0 + e);
}

vec2 sceneC(vec2 frag, vec2 r) {
  vec2 P = (frag + frag - r) / r.x;
  float z = 0.0;
  float d = 1e3;
  vec4 O = vec4(0.0);
  for (int k = 0; k < 39; k++) {
    if (d <= 1e-4) break;
    O = z * normalize(vec4(P, uZoom, 0.0)) - vec4(0.0, 4.0, 1.0, 0.0) / 4.5;
    d = 1.0 - sqrt(length(O * O));
    z += d;
  }
  return vec2(O.x, atan(O.z, O.y));
}

void mainImage(out vec4 o, vec2 C) {
  vec2 r = iResolution.xy;
  vec2 uv0 = (C + C - r) / r.x;
  float T = 0.1 * iTime * uSpeed + 9.0;
  float angRings = max(1.0, floor(6.28318530718 * max(uDensity, 0.05) + 0.5));
  vec2 Y = vec2(5e-3, 6.28318530718 / angRings);

  vec2 c0 = sceneC(C, r);
  vec2 cdx = sceneC(C + vec2(1.0, 0.0), r);
  vec2 cdy = sceneC(C + vec2(0.0, 1.0), r);
  vec2 dCx = cdx - c0;
  vec2 dCy = cdy - c0;
  dCx.y -= 6.28318530718 * floor(dCx.y / 6.28318530718 + 0.5);
  dCy.y -= 6.28318530718 * floor(dCy.y / 6.28318530718 + 0.5);
  vec2 fw = abs(dCx) + abs(dCy);
  C = c0;

  vec2 P = vec2(2.0, 1.0) * uv0 - (r / r.x) * vec2(0.0, 1.0);
  vec4 O = vec4(uBgColor * 90.0 * uBgGlow / (1e3 * dot(P, P) + 6.0), 0.0);

  float mGlow = 0.0;
  if (uMouseEnabled > 0.5) {
    vec2 mN = (iMouse + iMouse - r) / r.x;
    float md = length(uv0 - mN);
    mGlow = exp(-md * md / max(uMouseRadius * uMouseRadius, 1e-4)) * uMouseStrength;
    O.rgb += uMouseColor * mGlow * 0.25;
  }

  float zr = 5e-4 * uStreakWidth;
  vec2 rr = vec2(max(length(fw), 1e-5));
  float tail = 19.0 / max(uStreakLength, 0.05);

  for (int m = 0; m < 16; m++) {
    if (m >= uStreakCount) break;
    float jf = float(m) + 1.0;
    float ic = fract(sin(dot(vec2(jf, floor(C.x / Y.x + 0.5)), vec2(7.0, 11.0)) * 73.0));
    vec2 Pp = C - (T + T * ic) * vec2(0.0, 1.0);
    Pp -= floor(Pp / Y + 0.5) * Y;
    float h = fract(8663.0 * ic);
    vec3 col = palette(h);
    float weight = mix(1.5, 1.0 + sin(T + 7.0 * h + 4.0), uTwinkle);
    weight *= (1.0 + mGlow * 2.0);
    vec2 inner = vec2(length(max(Pp, vec2(-1.0, 0.0))), length(Pp) - zr) - zr;
    vec2 sm = vec2(1.0) - smoothstep(-rr, rr, inner);
    O.rgb += dot(sm, vec2(exp(tail * Pp.y), 3.0)) * col * weight;
    C.x += Y.x / 8.0;
  }

  vec3 colr = sqrt(tanhv(max(O.rgb * uGlow - vec3(0.04, 0.08, 0.02), 0.0)));
  o = vec4(colr, uOpacity);
}

void main() {
  vec4 color;
  mainImage(color, vUv * iResolution.xy);
  gl_FragColor = color;
}
`;

/**
 * React Bits' Lightfall: streaks of light falling down a tunnel, drawn by a fragment shader
 * (ogl) on a canvas filling its positioned parent. Ported to TypeScript with three changes:
 *
 * - Props other than `maxDevicePixelRatio` are read through a ref each frame, so changing one
 *   never rebuilds the WebGL context.
 * - The render loop sleeps while the canvas is off screen or the tab is hidden, and wakes
 *   when either flips, so the rest of the page pays nothing for it once scrolled past.
 * - Under reduced motion (or `paused`) it draws one frame and stops: a still of the effect
 *   rather than nothing. The light-mode branch of the original is dropped; the site is dark.
 */
export function Lightfall({
  colors = DEFAULT_COLORS,
  backgroundColor = '#0A29FF',
  speed = 0.5,
  streakCount = 2,
  streakWidth = 1,
  streakLength = 1,
  glow = 1,
  density = 0.6,
  twinkle = 1,
  zoom = 3,
  backgroundGlow = 0.5,
  opacity = 1,
  mouseInteraction = true,
  mouseStrength = 0.5,
  mouseRadius = 1,
  mouseDampening = 0.15,
  paused = false,
  maxDevicePixelRatio = 1.5,
  className,
}: LightfallProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();
  const animate = !reduced && !paused;

  const config = {
    colors,
    backgroundColor,
    speed,
    streakCount,
    streakWidth,
    streakLength,
    glow,
    density,
    twinkle,
    zoom,
    backgroundGlow,
    opacity,
    mouseInteraction,
    mouseStrength,
    mouseRadius,
    mouseDampening,
  };
  const configRef = useRef(config);
  configRef.current = config;

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    let renderer: Renderer;
    try {
      renderer = new Renderer({
        canvas,
        alpha: true,
        // A single full-screen triangle has no edges to smooth; MSAA would only cost fill rate.
        antialias: false,
        dpr: Math.min(window.devicePixelRatio || 1, maxDevicePixelRatio),
      });
    } catch {
      return;
    }
    const gl = renderer.gl;
    if (!gl) return;

    const initial = configRef.current;
    const palette = prepColors(initial.colors);
    const colorUniforms = palette.arr.map((value) => ({ value }));

    const uniforms = {
      iResolution: { value: [gl.drawingBufferWidth, gl.drawingBufferHeight, 1] },
      iMouse: { value: [0, 0] },
      iTime: { value: 0 },
      uColor0: colorUniforms[0],
      uColor1: colorUniforms[1],
      uColor2: colorUniforms[2],
      uColor3: colorUniforms[3],
      uColor4: colorUniforms[4],
      uColor5: colorUniforms[5],
      uColor6: colorUniforms[6],
      uColor7: colorUniforms[7],
      uColorCount: { value: palette.count },
      uBgColor: { value: hexToRgb(initial.backgroundColor) },
      uMouseColor: { value: palette.avg },
      uSpeed: { value: initial.speed },
      uStreakCount: { value: 1 },
      uStreakWidth: { value: initial.streakWidth },
      uStreakLength: { value: initial.streakLength },
      uGlow: { value: initial.glow },
      uDensity: { value: initial.density },
      uTwinkle: { value: initial.twinkle },
      uZoom: { value: initial.zoom },
      uBgGlow: { value: initial.backgroundGlow },
      uOpacity: { value: initial.opacity },
      uMouseEnabled: { value: 0 },
      uMouseStrength: { value: initial.mouseStrength },
      uMouseRadius: { value: initial.mouseRadius },
    };

    const program = new Program(gl, {
      vertex: VERTEX_SHADER,
      fragment: FRAGMENT_SHADER,
      uniforms,
      depthTest: false,
      depthWrite: false,
    });
    const mesh = new Mesh(gl, { geometry: new Triangle(gl), program });

    let paletteKey = '';
    const syncUniforms = () => {
      const current = configRef.current;
      const key = `${current.colors.join('|')}#${current.backgroundColor}`;
      if (key !== paletteKey) {
        paletteKey = key;
        const next = prepColors(current.colors);
        next.arr.forEach((value, i) => {
          colorUniforms[i].value = value;
        });
        uniforms.uColorCount.value = next.count;
        uniforms.uMouseColor.value = next.avg;
        uniforms.uBgColor.value = hexToRgb(current.backgroundColor);
      }
      uniforms.uSpeed.value = current.speed;
      uniforms.uStreakCount.value = Math.max(1, Math.min(16, Math.round(current.streakCount)));
      uniforms.uStreakWidth.value = current.streakWidth;
      uniforms.uStreakLength.value = current.streakLength;
      uniforms.uGlow.value = current.glow;
      uniforms.uDensity.value = current.density;
      uniforms.uTwinkle.value = current.twinkle;
      uniforms.uZoom.value = current.zoom;
      uniforms.uBgGlow.value = current.backgroundGlow;
      uniforms.uOpacity.value = current.opacity;
      uniforms.uMouseEnabled.value = current.mouseInteraction ? 1 : 0;
      uniforms.uMouseStrength.value = current.mouseStrength;
      uniforms.uMouseRadius.value = current.mouseRadius;
    };

    const mouseTarget = [0, 0];
    let raf = 0;
    let destroyed = false;
    let inView = true;
    let lastTime = 0;

    const shouldRun = () => animate && inView && document.visibilityState === 'visible';

    const render = (now: number) => {
      raf = 0;
      if (destroyed) return;
      syncUniforms();
      uniforms.iTime.value = now * 0.001;

      const { mouseDampening: tau } = configRef.current;
      if (tau > 0) {
        if (!lastTime) lastTime = now;
        const dt = (now - lastTime) / 1000;
        const factor = Math.min(1, 1 - Math.exp(-dt / Math.max(1e-4, tau)));
        const cur = uniforms.iMouse.value;
        cur[0] += (mouseTarget[0] - cur[0]) * factor;
        cur[1] += (mouseTarget[1] - cur[1]) * factor;
      }
      lastTime = now;

      renderer.render({ scene: mesh });
      if (shouldRun()) raf = requestAnimationFrame(render);
    };

    /** Draws a frame; keeps drawing while shouldRun() holds. */
    const wake = () => {
      if (raf || destroyed) return;
      lastTime = 0;
      raf = requestAnimationFrame(render);
    };

    const resize = () => {
      const rect = container.getBoundingClientRect();
      renderer.setSize(Math.max(1, rect.width), Math.max(1, rect.height));
      uniforms.iResolution.value = [gl.drawingBufferWidth, gl.drawingBufferHeight, 1];
      // Repaint at the new size even when frozen.
      wake();
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!configRef.current.mouseInteraction) return;
      const rect = canvas.getBoundingClientRect();
      const scale = renderer.dpr || 1;
      mouseTarget[0] = (event.clientX - rect.left) * scale;
      mouseTarget[1] = (rect.height - (event.clientY - rect.top)) * scale;
      if (configRef.current.mouseDampening <= 0) {
        uniforms.iMouse.value = [mouseTarget[0], mouseTarget[1]];
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') wake();
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(container);
    const intersectionObserver = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting;
      if (inView) wake();
    });
    intersectionObserver.observe(container);
    // On the window, not the canvas: the layer usually sits behind content with no pointer events.
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    resize();

    return () => {
      destroyed = true;
      cancelAnimationFrame(raf);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('visibilitychange', onVisibility);
      mesh.geometry.remove();
      program.remove();
    };
  }, [animate, maxDevicePixelRatio]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className={cn('absolute inset-0 overflow-hidden', className)}
    >
      <canvas ref={canvasRef} className="block h-full w-full select-none" />
    </div>
  );
}
